import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import InfoTooltip from '../components/InfoTooltip';
import 'react-datepicker/dist/react-datepicker.css';

const EmployeeManagement = () => {
  const { setPageTitle } = usePageContext();
  const [employees, setEmployees] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0, limit: 10 });
  const [filters, setFilters] = useState({ search: '', status: '', sortBy: 'createdAt', sortOrder: 'desc' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    client: '',
    vendor: '',
    joiningDate: new Date(),
    recruiter: '',
    clientRate: '',
    consultantRate: '',
    currentCondition: '',
    comments: ''
  });
  const { hasPermission } = useAuth();

  useEffect(() => {
    setPageTitle('Employee Management');
    if (!hasPermission('employee', 'read')) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const promises = [fetchEmployees()];
        
        if (hasPermission('recruiter', 'read')) promises.push(fetchRecruiters());
        if (hasPermission('condition', 'read')) promises.push(fetchConditions());
        if (hasPermission('client', 'read')) promises.push(fetchClients());
        if (hasPermission('vendor', 'read')) promises.push(fetchVendors());
        
        await Promise.all(promises);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasPermission]);

  useEffect(() => {
    if (hasPermission('employee', 'read')) {
      fetchEmployees();
    }
  }, [pagination.current, filters]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.limit,
        ...filters
      };
      const response = await axios.get('/api/employee', { params });
      setEmployees(Array.isArray(response.data.employees) ? response.data.employees : Array.isArray(response.data) ? response.data : []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('Error fetching employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const response = await axios.get('/api/recruiter');
      setRecruiters(response.data);
    } catch (error) {
      toast.error('Error fetching recruiters');
    }
  };

  const fetchConditions = async () => {
    try {
      const response = await axios.get('/api/condition');
      setConditions(response.data);
    } catch (error) {
      toast.error('Error fetching conditions');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/client');
      setClients(response.data);
    } catch (error) {
      toast.error('Error fetching clients');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendor');
      setVendors(response.data);
    } catch (error) {
      toast.error('Error fetching vendors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (editingEmployee) {
        await axios.put(`/api/employee/${editingEmployee._id}`, formData);
        toast.success('Employee updated successfully');
      } else {
        await axios.post('/api/employee', formData);
        toast.success('Employee created successfully');
      }
      setShowModal(false);
      setEditingEmployee(null);
      resetForm();
      setSelectedEmployees([]);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedEmployees.map(id => axios.delete(`/api/employee/${id}`)));
      toast.success(`${selectedEmployees.length} employees deleted successfully`);
      setSelectedEmployees([]);
      setShowDeleteModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error('Error deleting employees');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phoneNumber: '',
      client: '',
      vendor: '',
      joiningDate: new Date(),
      recruiter: '',
      clientRate: '',
      consultantRate: '',
      currentCondition: '',
      comments: ''
    });
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      ...employee,
      client: employee.client?._id || employee.client,
      vendor: employee.vendor?._id || employee.vendor,
      recruiter: employee.recruiter?._id || employee.recruiter,
      currentCondition: employee.currentCondition?._id || employee.currentCondition,
      joiningDate: new Date(employee.joiningDate)
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await axios.delete(`/api/employee/${id}`);
        toast.success('Employee deleted successfully');
        fetchEmployees();
      } catch (error) {
        toast.error('Error deleting employee');
      }
    }
  };

  const recruiterOptions = recruiters.map(r => ({ value: r._id, label: r.name }));
  const conditionOptions = conditions.map(c => ({ value: c._id, label: c.name }));
  const clientOptions = clients.map(c => ({ value: c.name, label: c.name }));
  const vendorOptions = vendors.map(v => ({ value: v.name, label: v.name }));



  if (!hasPermission('employee', 'read')) {
    return (
      <div className="alert alert-danger text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view employees.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1>Employee Management</h1>
            <p>Manage consultant profiles and payroll condition assignments</p>
          </div>
          {hasPermission('employee', 'create') && (
            <button 
              className="btn btn-light"
              onClick={() => setShowModal(true)}
            >
              <i className="fas fa-plus me-2"></i>
              Add Employee
            </button>
          )}
        </div>
      </div>

      {selectedEmployees.length > 0 && (
        <div className="selection-bar mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <span className="selection-count">{selectedEmployees.length} employees selected</span>
            <button className="btn btn-sm btn-light" onClick={() => setShowDeleteModal(true)}>
              <i className="fas fa-trash me-1"></i>Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text"><i className="fas fa-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search employees..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  setFilters({...filters, sortBy, sortOrder});
                }}
              >
                <option value="createdAt-desc">Newest First</option>
                <option value="createdAt-asc">Oldest First</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="clientRate-desc">Rate High-Low</option>
                <option value="clientRate-asc">Rate Low-High</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Table */}
      {loading ? (
        <div className="card">
          <div className="loading-overlay">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading employees...</p>
            </div>
          </div>
        </div>
      ) : employees.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h5>No employees found</h5>
            <p>Start by adding your first employee to the system.</p>
            {hasPermission('employee', 'create') && (
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                <i className="fas fa-plus me-2"></i>Add First Employee
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEmployees(employees.map(emp => emp._id));
                        } else {
                          setSelectedEmployees([]);
                        }
                      }}
                      checked={selectedEmployees.length === employees.length && employees.length > 0}
                    />
                  </th>
                  <th>Employee</th>
                  <th>Client</th>
                  <th>Recruiter</th>
                  <th>Pay Rate</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
            <tbody>
              {employees.map(employee => (
                <tr key={employee._id}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedEmployees.includes(employee._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEmployees([...selectedEmployees, employee._id]);
                        } else {
                          setSelectedEmployees(selectedEmployees.filter(id => id !== employee._id));
                        }
                      }}
                    />
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="avatar-circle me-3">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-semibold">{employee.name}</div>
                        <small className="text-muted">{employee.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-light text-dark">{employee.client?.name || employee.client || 'N/A'}</span>
                  </td>
                  <td>{employee.recruiter?.name || 'N/A'}</td>
                  <td>
                    <span className="fw-bold text-success">${employee.clientRate || employee.payRate || 0}</span>
                  </td>
                  <td>
                    <span className={`badge ${employee.isActive ? 'bg-success' : 'bg-danger'}`}>
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {hasPermission('employee', 'update') && (
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEdit(employee)}
                          title="Edit Employee"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {hasPermission('employee', 'delete') && (
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(employee._id)}
                          title="Delete Employee"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {((pagination.current - 1) * pagination.limit) + 1} to {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} entries
        </div>
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${pagination.current === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination({...pagination, current: pagination.current - 1})} disabled={pagination.current === 1}>
                Previous
              </button>
            </li>
            {[...Array(pagination.pages)].map((_, i) => (
              <li key={i + 1} className={`page-item ${pagination.current === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPagination({...pagination, current: i + 1})}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${pagination.current === pagination.pages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination({...pagination, current: pagination.current + 1})} disabled={pagination.current === pagination.pages}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingEmployee ? 'Edit Employee' : 'Add Employee'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Client</label>
                      <Select
                        options={clients.map(c => ({ value: c._id, label: c.name }))}
                        value={clients.map(c => ({ value: c._id, label: c.name })).find(c => c.value === formData.client)}
                        onChange={(option) => setFormData({...formData, client: option?.value || ''})}
                        placeholder="Select Client"
                        isClearable
                      />
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Vendor</label>
                      <Select
                        options={vendors.map(v => ({ value: v._id, label: v.name }))}
                        value={vendors.map(v => ({ value: v._id, label: v.name })).find(v => v.value === formData.vendor)}
                        onChange={(option) => setFormData({...formData, vendor: option?.value || ''})}
                        placeholder="Select Vendor"
                        isClearable
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Joining Date</label>
                      <DatePicker
                        selected={formData.joiningDate}
                        onChange={(date) => setFormData({...formData, joiningDate: date})}
                        className="form-control"
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Recruiter</label>
                      <Select
                        options={recruiterOptions}
                        value={recruiterOptions.find(r => r.value === formData.recruiter)}
                        onChange={(option) => setFormData({...formData, recruiter: option?.value || ''})}
                        isClearable
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Current Condition
                        <InfoTooltip 
                          title="Payroll Condition Assignment"
                          content="This determines how the employee's pay is calculated based on margin tiers, bonuses, and hikes. You can change this anytime and the system will track the history."
                        />
                      </label>
                      <Select
                        options={conditionOptions}
                        value={conditionOptions.find(c => c.value === formData.currentCondition)}
                        onChange={(option) => setFormData({...formData, currentCondition: option?.value || ''})}
                        placeholder="Select Condition"
                        isClearable
                      />
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Client Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.clientRate}
                        onChange={(e) => setFormData({...formData, clientRate: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Consultant Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.consultantRate}
                        onChange={(e) => setFormData({...formData, consultantRate: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  

                  
                  <div className="mb-3">
                    <label className="form-label">Comments</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.comments}
                      onChange={(e) => setFormData({...formData, comments: e.target.value})}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingEmployee ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingEmployee ? 'Update Employee' : 'Create Employee'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedEmployees.length} employee(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedEmployees.length} Employee(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;